import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { setGridLayout, setProductType } from "../ProductRangeSlice";
import { CategoryItem } from "./CategoryItem";
import { ProductSelectedItem } from "./ProductSelectedItem";
import { JSX, Key, useState } from "react";
import { ProductTypeEnum } from "../../../common/enums/ProductTypeEnum";
import { ProductIndexItemInterface } from "../../../common/interfaces/apiV2/shared/ProductIndexItemInterface";
import { setAddCollectionModal } from "../../../modals/addCollectionModal/AddCollectionModalSlice";
import { useGetUserInfoQuery } from "../../../app/rootReducer";
import { useProductRangeData } from "../hooks/useProductRangeData";
import { ProductRangeCategoryInterface } from "../../../common/utils/productRange/mergeCategoriesWithProducts";

import {
    ProductsLayoutSwitch,
    ProductsNav,
} from "@hatch-digital-nl/bravilor-storybook";
import { useStringTranslation } from "../../../common/hooks/useStringTranslation";

const CategoryItemSkeleton = (): JSX.Element => {
    return (
        <>
            {Array(4)
                .fill(0)
                .map((_, index) => (
                    <div className={"c-products-list__item "} key={index}>
                        <div className="c-products-list__item__header">
                            <span className="skeleton skeleton-title"></span>
                            <div className="c-products-list__item__header__icon">
                                <span></span>
                                <span></span>
                            </div>
                        </div>
                    </div>
                ))}
        </>
    );
};

export const Products = (): JSX.Element => {
    const dispatch = useAppDispatch();
    const userData = useGetUserInfoQuery();
    const { activeLanguage } = userData?.data || { activeLanguage: null };
    const { t, isLoading: isTranslationsLoading } = useStringTranslation();
    const [selectedActive, setSelectedActive] = useState(false);

    const { selectedProducts, gridLayout, productType, selectedFilters } =
        useAppSelector((state) => state.productRange);

    const { categories, isCategoriesLoading, isCountryUpdating } =
        useProductRangeData({
            productType,
            selectedFilters,
        });

    const translationsLoaded = !isTranslationsLoading && !!activeLanguage;

    const productNavItems = [
        {
            type: ProductTypeEnum.products,
            text: t("machines"),
            panelId: "machines",
        },
        {
            type: ProductTypeEnum.accessories,
            text: t("accessories"),
            panelId: "accessories",
        },
    ];

    return (
        <div className="c-products">
            <div className="c-products__nav">
                <ProductsNav
                    activeProductType={productType[0]}
                    onProductTypeChange={(type: string) =>
                        dispatch(setProductType(type as ProductTypeEnum))
                    }
                    isLoadingTranslations={isTranslationsLoading}
                    navItems={productNavItems}
                />

                <ProductsLayoutSwitch
                    gridLayout={gridLayout}
                    gridText={t("grid")}
                    listText={t("list")}
                    isLoadingTranslations={isTranslationsLoading}
                    onLayoutChange={() => dispatch(setGridLayout(!gridLayout))}
                />
            </div>

            <div className="c-products__selection">
                <div
                    className={
                        "c-products-selected__overlay " +
                        (selectedActive ? "is-active" : "")
                    }
                ></div>
                <div
                    className={
                        "c-products-selected " +
                        (selectedActive ? "is-active" : "")
                    }
                >
                    <div
                        className="c-products-selected__header"
                        onClick={() => setSelectedActive(!selectedActive)}
                    >
                        <div className="c-products-selected__header__left">
                            {translationsLoaded ? (
                                <>
                                    <span className="c-products-selected__header__bold">
                                        {t("selection")}(
                                        {selectedProducts.length}/50)
                                    </span>
                                    <span>
                                        {t(
                                            "select-products-to-save-to-a-collection-or-to-create-an-export",
                                        )}
                                    </span>
                                </>
                            ) : (
                                <div className="skeleton skeleton-text"></div>
                            )}
                        </div>
                        <div className="c-products-selected__header__right">
                            <img
                                src="/images/arrow-black.svg"
                                alt="arrow-down"
                            />
                        </div>
                    </div>

                    {!!selectedProducts && selectedProducts.length > 0 && (
                        <div className="c-products-selected__container">
                            <div className="c-products-selected__items">
                                {selectedProducts.map(
                                    (
                                        product: ProductIndexItemInterface,
                                        index: number,
                                    ) => (
                                        <ProductSelectedItem
                                            key={index}
                                            product={product}
                                        />
                                    ),
                                )}
                            </div>

                            <div className="c-products-selected__footer">
                                <div className="c-products-selected__footer__left">
                                    <button
                                        className="c-btn c-btn--primary"
                                        onClick={() => {
                                            dispatch(
                                                setAddCollectionModal({
                                                    showPopup: true,
                                                    selectedProductIds:
                                                        selectedProducts.map(
                                                            (
                                                                product: ProductIndexItemInterface,
                                                            ) => product.id,
                                                        ),
                                                }),
                                            );
                                        }}
                                    >
                                        <img
                                            src="/images/list-view-white.svg"
                                            alt="list"
                                        />
                                        <span>{t("add-to-collection")}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {isCategoriesLoading || isCountryUpdating ? (
                    <CategoryItemSkeleton />
                ) : categories.length > 0 ? (
                    <div className="c-products-list">
                        {categories.map(
                            (
                                category: ProductRangeCategoryInterface,
                                index: Key | null | undefined,
                            ) => (
                                <CategoryItem
                                    key={index}
                                    category={category}
                                    standardActive={categories.length === 1}
                                />
                            ),
                        )}
                    </div>
                ) : null}
            </div>
        </div>
    );
};
